// Title screen: neon title over the key art (or an animated skyline), play /
// settings / asset preview, and meta progress.

import Phaser from 'phaser';
import { SCENES } from '../config/constants';
import { COLORS } from '../config/palette';
import { save } from '../core/save';
import { SECTORS } from '../data/sectors';
import { Skyline, coverImage } from '../ui/backdrops';
import { MenuNav, TYPE, UI, createButton, footerHints, music, setMenuCursor, txt, unlockAudio } from '../ui/kit';

export class MenuScene extends Phaser.Scene {
  private nav!: MenuNav;
  private skyline: Skyline | null = null;
  private title!: Phaser.GameObjects.Text;
  private ghost!: Phaser.GameObjects.Text;
  private t = 0;

  constructor() {
    super(SCENES.MENU);
  }

  create(): void {
    setMenuCursor(this, true);
    this.skyline = null;
    if (this.textures.exists('art_title')) {
      const art = coverImage(this, 'art_title');
      this.tweens.add({ targets: art, scale: art.scale * 1.05, duration: 24000, yoyo: true, repeat: -1, ease: 'Sine.InOut' });
    } else {
      this.skyline = new Skyline(this);
    }
    // Darken the left side for the menu column.
    const shade = this.add.graphics();
    shade.fillGradientStyle(0x05070b, 0x05070b, 0x05070b, 0x05070b, 0.9, 0.0, 0.9, 0.0);
    shade.fillRect(0, 0, UI.W * 0.6, UI.H);

    const x = 150;
    this.ghost = txt(this, x + 4, 210, 'NEON CORE', { font: 'display', size: TYPE.hero, weight: 700, spacing: 14, color: COLORS.threat, alpha: 0.35 });
    this.title = txt(this, x, 206, 'NEON CORE', { font: 'display', size: TYPE.hero, weight: 700, spacing: 14, color: COLORS.uiText, glow: { color: COLORS.volt, blur: 24 } });
    txt(this, x + 6, 350, 'MECHA ACTION ROGUELITE', { font: 'display', size: TYPE.h3, spacing: 10, color: COLORS.volt });

    this.nav = new MenuNav(this, { layout: 'vertical' });
    const bx = x + 200;
    const play = createButton(this, bx, 500, { label: 'Play', width: 400, height: 72, variant: 'primary', accent: COLORS.volt, hint: 'confirm', onClick: () => this.go(SCENES.HANGAR) }, this.nav);
    createButton(this, bx, 590, { label: 'Versus', width: 400, height: 64, accent: COLORS.threat, sublabel: 'ONLINE', sublabelColor: COLORS.uiGood, onClick: () => this.go(SCENES.VERSUS_LOBBY) }, this.nav);
    createButton(this, bx, 670, { label: 'Settings', width: 400, height: 64, onClick: () => this.go(SCENES.SETTINGS, { returnTo: SCENES.MENU }) }, this.nav);
    createButton(this, bx, 750, { label: 'Asset preview', width: 400, height: 64, variant: 'secondary', onClick: () => this.go(SCENES.PREVIEW) }, this.nav);

    const d = save.data;
    const best = SECTORS[d.bestSector]?.name ?? '—';
    txt(this, x, UI.H - 150, `CORES ${d.cores}     RUNS ${d.runs}     WINS ${d.wins}     BEST ${d.runs ? best : '—'}`, { font: 'display', size: TYPE.label, spacing: 3, color: COLORS.uiDim });
    footerHints(this, [
      { action: 'navigateV', label: 'Select' },
      { action: 'confirm', label: 'Confirm' },
    ]);
    this.nav.focus(play, { silent: true });
    this.input.once('pointerdown', () => unlockAudio());
    this.input.keyboard?.once('keydown', () => unlockAudio());
    music('menu');
    this.cameras.main.fadeIn(500, 5, 7, 11);
  }

  private go(key: string, data?: object): void {
    unlockAudio();
    this.cameras.main.fadeOut(220, 5, 7, 11);
    this.cameras.main.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () => this.scene.start(key, data));
  }

  update(_t: number, delta: number): void {
    const dt = delta / 1000;
    this.t += dt;
    this.nav?.update();
    this.skyline?.update(dt);
    // Neon flicker + slight chromatic offset.
    const flick = Math.random() < 0.015 ? 0.55 : 1;
    this.title.setAlpha(flick);
    this.ghost.setX(154 + Math.sin(this.t * 1.7) * 3);
  }
}
